import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, Menu, X } from '../icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import type { IconProps } from '../icons';

export interface SidebarItem {
  label: string;
  path: string;
  icon: (props: IconProps) => JSX.Element;
  gated?: boolean;
  gateMessage?: string;
  group?: string;
}

interface SidebarProps {
  items: SidebarItem[];
  title: string;
  subtitle?: string;
  roleIndicator?: string;
  headerSlot?: React.ReactNode;
}

function renderLabel(label: string) {
  if (!label.includes('™')) return label;
  const [before, after] = label.split('™');
  return <>{before}<sup className="text-[8px] align-super">™</sup>{after}</>;
}

export function Sidebar({ items, title, subtitle, roleIndicator, headerSlot }: SidebarProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userName, setUserName] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('users')
      .select('full_name, email')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        setUserName(data?.full_name || data?.email || user.email || '');
      });
  }, [user?.id, user?.email]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-chocolate-200/30">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sage-500 text-xl">&#10022;</span>
          <h2 className="text-lg font-bold text-chocolate-800 tracking-tight">{title}</h2>
        </div>
        {subtitle && <p className="text-xs text-chocolate-400 ml-7">{subtitle}</p>}
      </div>

      {headerSlot && (
        <div className="px-4 pt-4 pb-2">
          {headerSlot}
        </div>
      )}

      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
        {items.map((item, index) => {
          const prevItem = items[index - 1];
          const showGroupHeader = item.group !== undefined && item.group !== prevItem?.group;

          const groupHeader = showGroupHeader ? (
            <div className={`px-3 pb-1 ${index === 0 ? 'pt-1' : 'pt-4'}`}>
              <span className="text-[10px] font-bold uppercase tracking-widest text-chocolate-400/70">{item.group}</span>
            </div>
          ) : null;

          if (item.gated) {
            return (
              <div key={item.path}>
                {groupHeader}
                <div className="flex items-center gap-3 px-3 py-2 rounded-lg text-chocolate-300 cursor-not-allowed">
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm">{renderLabel(item.label)}</span>
                    {item.gateMessage && (
                      <p className="text-[10px] text-chocolate-300 truncate">{item.gateMessage}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={item.path}>
              {groupHeader}
              <NavLink
                to={item.path}
                end={item.path === '/partner' || item.path === '/coach' || item.path === '/dashboard' || item.path === '/tr/coach' || item.path === '/tr/partner' || item.path === '/dashboard/progress'}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-sage-100 text-sage-600'
                      : 'text-chocolate-600 hover:bg-cream-100 hover:text-chocolate-800'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-sage-600' : 'text-chocolate-400'}`} />
                    <span className="truncate">{renderLabel(item.label)}</span>
                  </>
                )}
              </NavLink>
            </div>
          );
        })}
      </nav>

      <div className="border-t border-chocolate-200/30 p-4 space-y-3">
        <div className="flex items-center gap-3 px-2">
          <div className="w-8 h-8 rounded-full bg-sage-500 flex items-center justify-center text-xs font-bold text-white uppercase">
            {userName.charAt(0) || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-chocolate-800 font-medium truncate">{userName}</p>
            <p className="text-[11px] text-chocolate-400 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-chocolate-500 hover:bg-cream-100 hover:text-chocolate-700 transition-all"
        >
          <LogOut className="w-5 h-5" />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:w-64 bg-cream-50 border-r border-chocolate-200/20 z-30">
        {sidebarContent}
      </aside>

      {/* Mobile header bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-cream-50 border-b border-chocolate-200/20 z-40 flex items-center px-4 gap-3">
        <button
          onClick={() => setMobileOpen(true)}
          className="p-2 rounded-lg text-chocolate-600 hover:bg-cream-200 transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
        <h1 className="text-chocolate-800 font-semibold text-sm">{title}</h1>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-40 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed inset-y-0 left-0 w-72 bg-cream-50 border-r border-chocolate-200/20 z-50 lg:hidden"
            >
              <div className="absolute top-3 right-3">
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-2 rounded-lg text-chocolate-400 hover:bg-cream-200 hover:text-chocolate-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
